import type { DataTableColumn } from "@vjal/ui";
import { evaluatePermissionGrant } from "../domain/sensitive-surfaces";
import type { CoachingRole } from "../domain/roles";
import { UsersPage } from "./users/users-page";
import { UserDetailPage } from "./users/user-detail-page";
import type { ProductScreenContract, TableRow } from "./product-route-contracts";

// ── Users (Wave 1 — REBUILT as gold-pattern per-screen pages) ──────────────
// The thin contract-surface user management was replaced by real per-screen
// pages on the gold pattern: a UsersPage list (DataTable + role/status filters +
// role-gated create/edit/deactivate modals) and a UserDetailPage record, each
// routed through useHonestQuery -> <HonestState>. The /users route is STAFF-ONLY
// (super_admin / admin) at the route manifest; granting the `admin` role
// requires super_admin (enforced in the form's role picker + server re-check).
// The contract factories below preserve the route registration + provenance
// metadata and render the new pages via `customBody`.

const grantTargets: CoachingRole[] = ["coach", "company_admin", "participant", "admin", "super_admin"];

const grantRows: TableRow[] = grantTargets.map((targetRole) => grantRow(targetRole));


function focusedDefaults(): Pick<
  ProductScreenContract,
  "requiredViewports" | "surfaceMode" | "closureState"
> {
  return {
    requiredViewports: ["desktop", "mobile"],
    surfaceMode: "focused",
    closureState: "focused",
  };
}

export function userManagementScreen(): ProductScreenContract {
  return {
    title: () => "Users",
    primaryJob: "Review every staff and coach user in scope — filter by role and status, and (for staff) create, edit, or deactivate.",
    secondaryJob: "Granting the admin role requires a super admin; non-staff roles are denied the user route entirely.",
    workTitle: "User directory",
    rows: grantRows,
    columns: grantColumns(),
    states: ["loading", "empty", "permission-denied", "unavailable", "error", "grant-blocked"],
    apiFamilies: ["user-service", "permission contracts", "history-service"],
    roleDeltas: "Super admin grants any role incl. admin; admin manages users but cannot grant admin/super_admin; other roles are route-denied.",
    extensionSlots: ["permission templates", "view-as audit", "role history", "deactivate/reactivate confirmation"],
    mobilePosture: "User filter bar, role/status chips, and table rows stack with no horizontal overflow.",
    evidenceRequirement: "Playwright must prove staff create/edit/deactivate, admin cannot grant admin/super_admin, denied-role not-found, and mobile no-overflow.",
    surfaceMode: "focused",
    customBody: UsersPage
  };
}

export function userDetailScreen(): ProductScreenContract {
  return {
    ...userManagementScreen(),
    primaryJob: "Inspect one user's role, status, and assigned scope before editing or deactivating them.",
    workTitle: "User record",
    ...focusedDefaults(),
    customBody: UserDetailPage
  };
}

function grantRow(targetRole: CoachingRole): TableRow {
  const decision = evaluatePermissionGrant("admin", targetRole);
  return {
    id: targetRole,
    targetRole,
    grant: decision.allowed ? "allowed" : "blocked",
    reason: decision.label
  };
}

function grantColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "targetRole", header: "Target role" },
    { key: "grant", header: "Grant" },
    { key: "reason", header: "Reason" }
  ];
}
